export function formatBytes(n) {
  const bytes = Number(n) || 0;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function emitProgress(onProgress, payload, lastRef) {
  if (!onProgress) return;
  const percent = payload.percent ?? 0;
  const phase = payload.phase || 'download';
  const key = `${phase}:${payload.indeterminate ? 'i' : percent}:${Math.floor((payload.loaded || 0) / 262144)}`;
  if (lastRef.current === key) return;
  lastRef.current = key;
  onProgress(payload);
}

/**
 * Read a fetch Response body with byte progress. Returns an ArrayBuffer.
 */
export async function readResponseWithProgress(response, onProgress) {
  const total = Number(response.headers.get('content-length')) || 0;
  const lastRef = { current: '' };

  if (!response.body?.getReader) {
    const buffer = await response.arrayBuffer();
    emitProgress(
      onProgress,
      { loaded: buffer.byteLength, total: buffer.byteLength, percent: 100, indeterminate: false, phase: 'download' },
      lastRef
    );
    return buffer;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    loaded += value.byteLength;
    const indeterminate = total <= 0;
    const percent = indeterminate ? 0 : Math.min(99, Math.round((loaded / total) * 100));
    emitProgress(
      onProgress,
      { loaded, total, percent, indeterminate, phase: 'download' },
      lastRef
    );
  }

  const buffer = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    buffer.set(chunk, offset);
    offset += chunk.byteLength;
  }

  emitProgress(
    onProgress,
    { loaded, total: total || loaded, percent: 100, indeterminate: false, phase: 'download' },
    lastRef
  );
  return buffer.buffer;
}

export async function fetchWithProgress(url, { signal, onProgress, cache = 'no-store', credentials = 'same-origin' } = {}) {
  let response;
  try {
    response = await fetch(url, { signal, cache, credentials });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new Error(`Failed to fetch ${url}: ${err?.message || err}`);
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} ${response.statusText || ''}`.trim());
  }
  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  const buffer = await readResponseWithProgress(response, onProgress);
  return { buffer, contentType };
}
